import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../../config';

const API = `${BACKEND_URL}/api`;
const ACHIEVEMENT_ID = 'pick-right-line';

const PickRightLine = () => {
  const navigate = useNavigate();
  const [showOffer, setShowOffer] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [resolved, setResolved] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [highlightHints, setHighlightHints] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const maxAttempts = 2;
  const correctLineId = 'C';

  const lines = [
    { id: 'A', label: 'DATA ROUTE A', desc: 'SECURE / ENCRYPTED CHANNEL' },
    { id: 'B', label: 'DATA ROUTE B', desc: 'INTERNATIONAL NODE / HIGH LATENCY' },
    { id: 'C', label: 'DATA ROUTE C', desc: 'LOCAL OPERATIONS / DIRECT LINK', correct: true },
    { id: 'D', label: 'DATA ROUTE D', desc: 'BACKUP RELAY / SECONDARY PATH' }
  ];

  const handleLineClick = (lineId) => {
    if (resolved) return;
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);
    setSelectedLine(lineId);

    if (lineId === correctLineId) {
      onCorrect();
    } else {
      onIncorrect(newAttempts);
    }
  };

  const onCorrect = async () => {
    setResolved(true);
    setStatusText('Correct. Local operations node matches the header route.');

    // Store achievement in sessionStorage
    sessionStorage.setItem('pendingAchievement', ACHIEVEMENT_ID);

    setTimeout(() => {
      setShowOffer(true);
      setTimeout(() => {
        navigate(`/scan?achievement=${ACHIEVEMENT_ID}`);
      }, 1300);
    }, 1100);
  };

  const onIncorrect = (currentAttempts) => {
    if (currentAttempts < maxAttempts) {
      setStatusText('Not quite. Check the ROUTE and NOTICE lines above — they describe the node type.');
      setHighlightHints(true);
    } else {
      setResolved(true);
      setSelectedLine(correctLineId);
      setStatusText('System override: matching LOCAL OPERATIONS NODE with header. Routing file anyway.');
      setHighlightHints(true);
      
      // Store achievement even on fail
      sessionStorage.setItem('pendingAchievement', ACHIEVEMENT_ID);
      
      setTimeout(() => {
        setShowOffer(true);
        setTimeout(() => {
          navigate(`/scan?achievement=${ACHIEVEMENT_ID}`);
        }, 1300);
      }, 1200);
    }
  };

  return (
    <div style={styles.wrap}>
      {!showOffer ? (
        <div style={styles.terminal}>
          <div style={styles.header}>
            <div style={styles.dots}>
              <span style={styles.dot}></span>
              <span style={styles.dot}></span>
              <span style={styles.dot}></span>
            </div>
            <div style={styles.headerLabel}>F.B.I. ARCHIVAL GATEWAY</div>
          </div>
          
          <div style={styles.body}>
            <div style={{...styles.line, ...(highlightHints ? styles.highlight : {})}}>
              <span style={styles.label}>ROUTE:</span>
              <span style={styles.value}> OPS-NODE C / LOCAL OPERATIONS</span>
            </div>
            <div style={{...styles.line, ...(highlightHints ? styles.highlight : {})}}>
              <span style={styles.label}>DOCUMENT:</span>
              <span style={styles.value}> TAPPD // LOCAL BUSINESS OFFER</span>
            </div>
            <div style={styles.line}>
              <span style={styles.label}>STATUS:</span>
              <span style={{...styles.value, color: '#ef4444'}}> CLASSIFIED</span>
            </div>
            <div style={{...styles.line, ...(highlightHints ? styles.highlight : {})}}>
              <span style={styles.label}>NOTICE:</span>
              <span style={styles.value}> SELECT ROUTE MATCHING LOCAL OPERATIONS CONTEXT</span>
            </div>
            
            <div style={styles.gameTitle}>Select the Correct Data Route</div>
            <div style={styles.gameInstruction}>Choose the data line that matches the local operations context.</div>
            
            <div style={styles.linesGrid}>
              {lines.map((line) => (
                <div
                  key={line.id}
                  style={{
                    ...styles.dataLine,
                    ...(selectedLine === line.id && resolved ? styles.selected : {}),
                    ...(resolved && line.id !== correctLineId ? styles.disabled : {})
                  }}
                  onClick={() => handleLineClick(line.id)}
                >
                  <div style={styles.lineLabel}>{line.label}</div>
                  <div style={styles.lineDesc}>{line.desc}</div>
                </div>
              ))}
            </div>
            
            {statusText && (
              <div style={{
                ...styles.statusText,
                color: resolved ? '#10b981' : '#ef4444'
              }}>
                {statusText}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={styles.offer}>
          <small style={{color: '#10b981', fontWeight: 700}}>DATA ROUTE VERIFIED</small>
          <div style={styles.offerTitle}>Connection established.</div>
          <p>You've matched the correct operational node. Your <strong>exclusive Tappd offer</strong> is ready.</p>
          <p style={styles.tagline}>Routing to reward node…</p>
        </div>
      )}
    </div>
  );
};

const styles = {
  wrap: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: '"Courier New", monospace',
  },
  terminal: {
    background: '#1e293b',
    borderRadius: '12px',
    maxWidth: '600px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    border: '1px solid #334155',
  },
  header: {
    background: '#334155',
    padding: '12px 16px',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  dots: { display: 'flex', gap: '6px' },
  dot: { width: '12px', height: '12px', borderRadius: '50%', background: '#64748b' },
  headerLabel: { color: '#e2e8f0', fontSize: '14px', fontWeight: 700, letterSpacing: '1px' },
  body: { padding: '24px', color: '#10b981', fontSize: '14px', lineHeight: '1.8' },
  line: { marginBottom: '8px', transition: 'background 0.3s' },
  highlight: { background: 'rgba(251, 191, 36, 0.1)', padding: '4px', borderRadius: '4px' },
  label: { color: '#fbbf24', fontWeight: 700 },
  value: { color: '#10b981' },
  gameTitle: { color: '#fbbf24', fontSize: '18px', fontWeight: 700, marginTop: '24px', marginBottom: '12px', textTransform: 'uppercase' },
  gameInstruction: { color: '#cbd5e1', fontSize: '14px', marginBottom: '20px' },
  linesGrid: { display: 'grid', gap: '12px', marginBottom: '20px' },
  dataLine: {
    padding: '16px',
    background: '#0f172a',
    border: '2px solid #334155',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  selected: { borderColor: '#10b981', background: 'rgba(16, 185, 129, 0.1)' },
  disabled: { opacity: 0.4, cursor: 'not-allowed' },
  lineLabel: { color: '#fbbf24', fontSize: '14px', fontWeight: 700, marginBottom: '4px' },
  lineDesc: { color: '#94a3b8', fontSize: '12px' },
  statusText: { fontSize: '14px', fontWeight: 600, textAlign: 'center', marginTop: '16px' },
  offer: { maxWidth: '600px', background: 'white', padding: '40px', borderRadius: '12px', textAlign: 'center', color: '#1e293b' },
  offerTitle: { fontSize: '28px', fontWeight: 700, margin: '16px 0', fontFamily: 'Fraunces, serif' },
  tagline: { color: '#64748b', fontStyle: 'italic', marginTop: '16px' },
};

export default PickRightLine;